/**
 * Guards against open redirects: parses `next` against the current origin
 * and only honours it if it resolves to that same origin. A prefix check
 * (`startsWith("/")`) is not enough — the WHATWG URL parser normalises
 * backslashes to slashes and strips TAB/LF/CR, so payloads like
 * `/\evil.example` or `/\t/\tevil.example` would still pass a naive check
 * but resolve to a cross-origin URL. Parsing with `URL` and comparing
 * `origin` closes that gap.
 *
 * Recomposing `pathname + search + hash` isn't safe on its own either:
 * `u.pathname` can itself start with `//`, which browsers/routers treat as
 * a protocol-relative (i.e. cross-origin) URL. Payloads that produce this:
 *   - `/.//evil.example`        → pathname `//evil.example`
 *   - `/..//evil.example`       → pathname `//evil.example`
 *   - `https://app.x//evil.example` (same-origin host, path `//evil.example`)
 * so the recomposed path is rejected whenever it starts with `//`, even
 * though it already starts with `/`.
 *
 * Bypass payloads this guard defeats (verified against all 7):
 *   1. `https://evil.example`            — different origin
 *   2. `//evil.example`                  — protocol-relative, different origin
 *   3. `/\evil.example`                  — backslash normalised to `/`, then `//`
 *   4. `/\t/\tevil.example`              — control chars stripped, then `//`
 *   5. `/.//evil.example`                — resolves to pathname `//evil.example`
 *   6. `/..//evil.example`               — resolves to pathname `//evil.example`
 *   7. `https://app.x//evil.example`     — same-origin host, `//` path
 * Legit deep links (`/dashboard`, `/subcommunities/foo/events?event=1`, …)
 * are unaffected since their pathname always starts with a single `/`.
 */
export function safeNextPath(next: string | null): string | null {
  if (!next) return null;
  try {
    const u = new URL(next, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    const path = `${u.pathname}${u.search}${u.hash}`;
    return path.startsWith("/") && !path.startsWith("//") ? path : null;
  } catch {
    return null;
  }
}
